import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import AmbientGlow from "@/components/AmbientGlow";
import {
    ConsultVignette,
    RemoteVignette,
    SmallBizVignette,
    SoloVignette,
    StudentVignette,
    TeamVignette,
    VisitorVignette,
} from "@/components/audience/AudienceVignettes";

const EASE = [0.22, 1, 0.36, 1];

const AUDIENCES = [
    {
        num: "01",
        id: "freelancers",
        title: "Freelancers",
        desc: "Work independently from a professional environment built for focus.",
        Visual: SoloVignette,
        span: "lg:col-span-5",
        visualH: "h-40",
    },
    {
        num: "02",
        id: "startups",
        title: "Startups",
        desc: "Give your growing team a professional workspace without the complexity of a traditional office.",
        Visual: TeamVignette,
        span: "lg:col-span-7",
        visualH: "h-52",
        featured: true,
    },
    {
        num: "03",
        id: "remote-employees",
        title: "Remote Employees",
        desc: "Get out of the home office and into a productive professional environment.",
        Visual: RemoteVignette,
        span: "lg:col-span-5",
        visualH: "h-40",
    },
    {
        num: "04",
        id: "consultants",
        title: "Consultants",
        desc: "A professional setting to work, meet clients and build your business.",
        Visual: ConsultVignette,
        span: "lg:col-span-4",
        visualH: "h-32",
    },
    {
        num: "05",
        id: "small-businesses",
        title: "Small Businesses",
        desc: "Dedicated workspace solutions for growing teams.",
        Visual: SmallBizVignette,
        span: "lg:col-span-7",
        visualH: "h-44",
    },
    {
        num: "06",
        id: "students-professionals",
        title: "Students & Professionals",
        desc: "A comfortable environment for focused work and productivity.",
        Visual: StudentVignette,
        span: "lg:col-span-4",
        visualH: "h-32",
    },
    {
        num: "07",
        id: "visiting-professionals",
        title: "Visiting Professionals",
        desc: "A professional workspace while you're in Bikaner.",
        Visual: VisitorVignette,
        span: "lg:col-span-4",
        visualH: "h-32",
    },
];

const ORDER = ["startups", "freelancers", "remote-employees", "small-businesses", "consultants", "students-professionals", "visiting-professionals"];
const SORTED = ORDER.map((id) => AUDIENCES.find((a) => a.id === id));

export default function WhoIsItFor() {
    return (
        <section
            id="who-is-it-for"
            data-testid="audience-section"
            className="cv-auto relative overflow-hidden border-y border-slate-200/60 bg-white py-20 sm:py-28"
        >
            <AmbientGlow color="#2B5CAB" side="left" className="-left-40 top-24 h-[440px] w-[440px]" />

            <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
                {/* Intro */}
                <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.7, ease: EASE }}
                    >
                        <p
                            data-testid="audience-eyebrow"
                            className="inline-flex items-center gap-2.5 rounded-full border border-brand-purple/15 bg-canvas px-4 py-2 text-[11px] font-bold uppercase tracking-[0.24em] text-ink/60"
                        >
                            <span className="h-1.5 w-1.5 rounded-full bg-gradient-to-r from-brand-purple to-brand-teal" />
                            Built for the Way You Work
                        </p>
                        <h2 className="mt-6 max-w-2xl text-4xl font-bold leading-[1.08] tracking-tight text-ink sm:text-5xl">
                            Made for Ambitious People &amp; <span className="text-gradient">Growing Businesses.</span>
                        </h2>
                    </motion.div>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.7, ease: EASE, delay: 0.1 }}
                        className="max-w-md text-sm font-medium leading-relaxed text-ink/55 md:text-base"
                    >
                        Whether you work independently, lead a growing team or need a professional
                        place to meet, there's a workspace for you at Work Island.
                    </motion.p>
                </div>

                {/* Editorial bento */}
                <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-12">
                    {SORTED.map((audience, i) => (
                        <motion.article
                            key={audience.id}
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-50px" }}
                            transition={{ duration: 0.65, ease: EASE, delay: (i % 3) * 0.08 }}
                            whileHover={{ y: -4 }}
                            data-testid={`audience-card-${audience.id}`}
                            className={`audience-card group relative overflow-hidden rounded-3xl border border-slate-200/80 bg-white transition-shadow duration-500 hover:shadow-[0_28px_60px_-32px_rgba(22,50,79,0.4)] sm:col-span-1 ${audience.span}`}
                        >
                            <div className={`relative ${audience.visualH} overflow-hidden bg-gradient-to-br from-[#F6F7FB] to-[#EDF0F7]`}>
                                <div aria-hidden="true" className="blueprint-grid absolute inset-0 opacity-70" />
                                <div className="relative h-full transition-transform duration-700 ease-out group-hover:scale-[1.04]">
                                    <audience.Visual />
                                </div>
                            </div>
                            <div className="relative p-6">
                                <div className="flex items-start justify-between gap-4">
                                    <div className="flex items-start gap-3.5">
                                        <span className="aud-num pt-0.5 text-[13px] font-bold tracking-wider text-slate-300">
                                            {audience.num}
                                        </span>
                                        <div>
                                            <h3 className={`font-bold tracking-tight text-ink ${audience.featured ? "text-2xl" : "text-lg"}`}>
                                                {audience.title}
                                            </h3>
                                            <p className="mt-1.5 max-w-[44ch] text-sm font-medium leading-relaxed text-ink/50">
                                                {audience.desc}
                                            </p>
                                        </div>
                                    </div>
                                    <ArrowUpRight
                                        size={17}
                                        strokeWidth={2.4}
                                        aria-hidden="true"
                                        className="mt-1 shrink-0 text-slate-300 transition-all duration-500 group-hover:translate-x-0.5 group-hover:text-brand-teal"
                                    />
                                </div>
                            </div>
                        </motion.article>
                    ))}
                </div>

                {/* Signature copy + CTA */}
                <motion.div
                    initial={{ opacity: 0, y: 22 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.75, ease: EASE }}
                    className="mt-20 text-center"
                >
                    <p className="mx-auto max-w-3xl text-3xl font-bold leading-[1.15] tracking-tight text-ink sm:text-4xl lg:text-[44px]">
                        Whatever your work looks like,
                        <br />
                        <span className="text-gradient">there's a place for it here.</span>
                    </p>
                    <button
                        data-testid="audience-find-workspace-btn"
                        onClick={() => scrollToId("#workspaces")}
                        className="btn-gradient group mt-8 inline-flex items-center gap-2.5 rounded-full px-7 py-4 text-[15px] font-bold text-white"
                    >
                        Find Your Workspace
                        <ArrowRight size={17} strokeWidth={2.6} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </button>
                </motion.div>
            </div>
        </section>
    );
}
